import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Loader2, CheckCircle2, AlertCircle, ArrowRight, LogIn } from 'lucide-react';
import { motion } from 'framer-motion';

const LOGO_URL = '/logo.webp';
const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Public post-Stripe-checkout landing page for nutrition plan purchases.
 *
 * Public route (no ProtectedRoute) so paying customers on mobile aren't
 * bounced to login when their auth cookie gets stripped on the cross-site
 * redirect back from Stripe. The webhook generates the plan server-side;
 * this page either:
 *   - calls the authed verify endpoint (fast inline confirm + plan details), or
 *   - calls the public status endpoint (just confirms "paid") and asks the
 *     user to log in to see their new plan.
 */
export const NutritionPurchaseSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const { user, api } = useAuth();
  const navigate = useNavigate();

  const [state, setState] = useState({ status: 'verifying', plan: null, error: null });
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const sessionId = searchParams.get('session_id');
    const purchaseId = searchParams.get('purchase_id');
    if (!sessionId || !purchaseId) {
      setState({ status: 'error', plan: null, error: 'Missing payment info in URL.' });
      return;
    }

    const verifyAuthed = async () => {
      try {
        const res = await api().post('/nutrition-plans/purchase/verify', {
          session_id: sessionId,
          purchase_id: purchaseId,
        });
        if ((res.data.status === 'completed' || res.data.status === 'paid') && res.data.plan) {
          setState({ status: 'completed', plan: res.data.plan, error: null });
        } else if (res.data.status === 'unpaid') {
          setState({ status: 'unpaid', plan: null, error: 'Payment not completed. Please try again.' });
        } else {
          // Plan still generating server-side — retry once after a short delay
          setTimeout(verifyAuthed, 3000);
        }
      } catch (err) {
        // Fall back to public confirm if auth fails (cookie stripped on mobile redirect)
        verifyPublic();
        console.debug('verify auth fallback:', err.message);
      }
    };

    const verifyPublic = async () => {
      try {
        const res = await fetch(
          `${API}/api/payments/stripe/status?session_id=${encodeURIComponent(sessionId)}`,
          { credentials: 'include' },
        );
        const data = await res.json();
        if (data.paid) {
          setState({ status: 'paid_anon', plan: null, error: null });
        } else {
          setState({ status: 'unpaid', plan: null, error: 'Payment not completed yet.' });
        }
      } catch {
        setState({ status: 'error', plan: null, error: 'Could not confirm payment.' });
      }
    };

    if (user) verifyAuthed();
    else verifyPublic();
  }, [api, searchParams, user]);

  return (
    <div className="min-h-screen bg-black" data-testid="nutrition-success-page">
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="container mx-auto px-6 py-4 flex items-center justify-center">
          <Link to={user ? '/dashboard' : '/'}>
            <img src={LOGO_URL} alt="Ascend & Surpass" className="h-10 w-10" />
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-6 py-16">
        <div className="max-w-2xl mx-auto bg-card border border-border p-8 text-center">
          {state.status === 'verifying' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="py-8">
              <Loader2 className="h-16 w-16 text-primary animate-spin mx-auto mb-6" />
              <h2 className="font-heading text-2xl text-white uppercase tracking-wider mb-2">Confirming payment</h2>
              <p className="text-muted-foreground">Your AI chef is prepping your personalized meal plan…</p>
              <p className="text-muted-foreground text-sm mt-2">This can take 30–60 seconds.</p>
            </motion.div>
          )}

          {state.status === 'completed' && (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
              <div className="h-20 w-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="h-10 w-10 text-green-400" />
              </div>
              <h2 className="font-heading text-3xl text-white uppercase tracking-wider mb-2">
                You're set, <span className="text-primary">Champion</span>!
              </h2>
              <p className="text-muted-foreground mb-6">
                Your {state.plan?.duration_weeks}-week nutrition plan is ready to go.
              </p>

              <div className="bg-black border border-border p-4 mb-6 text-left">
                <p className="text-xs text-muted-foreground font-heading uppercase tracking-wider mb-2">Plan Overview</p>
                <p className="text-white text-sm mb-2">
                  <strong className="text-primary">{state.plan?.program_title}</strong>
                </p>
                {state.plan?.overview?.strategy && (
                  <p className="text-muted-foreground text-sm italic">"{state.plan.overview.strategy}"</p>
                )}
                {state.plan?.overview?.meals_per_day && (
                  <p className="text-muted-foreground text-xs mt-2">
                    {state.plan.overview.meals_per_day} meals/day · {state.plan.overview.daily_calories_start}–{state.plan.overview.daily_calories_end} kcal range
                  </p>
                )}
              </div>

              <div className="space-y-3">
                <Button
                  size="lg"
                  onClick={() => navigate('/nutrition-plans/my')}
                  className="bg-primary text-black font-heading tracking-wider uppercase hover:bg-primary/90 w-full"
                  data-testid="view-plan-btn"
                >
                  View My Plan <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
                <Link to="/dashboard">
                  <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/10 font-heading tracking-wider uppercase" data-testid="nutrition-success-dashboard-btn">
                    Back to Dashboard
                  </Button>
                </Link>
              </div>
            </motion.div>
          )}

          {/* Paid confirmed, but user is signed out (mobile cookie drop) */}
          {state.status === 'paid_anon' && (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
              <div className="h-20 w-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="h-10 w-10 text-green-400" />
              </div>
              <h2 className="font-heading text-3xl text-white uppercase tracking-wider mb-2">
                Payment <span className="text-primary">Confirmed</span>!
              </h2>
              <p className="text-muted-foreground mb-6">
                Your nutrition plan is being generated. Log in to view it.
              </p>
              <Link to="/login">
                <Button
                  size="lg"
                  className="bg-primary text-black font-heading tracking-wider uppercase hover:bg-primary/90 w-full"
                  data-testid="nutrition-success-login-btn"
                >
                  <LogIn className="h-4 w-4 mr-2" /> Log In to View Plan
                </Button>
              </Link>
            </motion.div>
          )}

          {(state.status === 'unpaid' || state.status === 'error') && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="h-20 w-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="h-10 w-10 text-red-400" />
              </div>
              <h2 className="font-heading text-2xl text-white uppercase tracking-wider mb-2">Something went wrong</h2>
              <p className="text-muted-foreground mb-6">{state.error}</p>
              <Link to={user ? '/nutrition-plans' : '/login'}>
                <Button className="bg-primary text-black">{user ? 'Try Again' : 'Log In'}</Button>
              </Link>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
};
